import type { Capability } from './permissions.ts';

// Movimientos de stock. El stock de un producto es la suma de sus movimientos (ledger):
// nunca se pisa un número, así dos dispositivos que venden sin conexión no se pierden ventas.
// Mismas reglas que el CHECK y la política RLS de stock_movements en SQL.

export const MOVEMENT_TYPES = {
  initial: { label: 'Stock inicial', sign: 'any', permission: 'adjust_stock' },
  purchase: { label: 'Compra', sign: 'in', permission: 'receive_purchases' },
  purchase_void: { label: 'Compra anulada', sign: 'out', permission: 'receive_purchases' },
  supplier_return: { label: 'Devolución a proveedor', sign: 'out', permission: 'receive_purchases' },
  adjustment: { label: 'Ajuste', sign: 'any', permission: 'adjust_stock' },
  shrinkage: { label: 'Merma', sign: 'out', permission: 'adjust_stock' },
  count: { label: 'Toma de inventario', sign: 'any', permission: 'adjust_stock' },
  sale: { label: 'Venta', sign: 'out', permission: 'sell' },
  sale_void: { label: 'Venta anulada', sign: 'in', permission: 'void_sales' },
  customer_return: { label: 'Devolución de cliente', sign: 'in', permission: 'void_sales' },
} as const satisfies Record<string, { label: string; sign: 'in' | 'out' | 'any'; permission: Capability }>;

export type MovementType = keyof typeof MOVEMENT_TYPES;

/** Motivos de merma más comunes (el usuario puede escribir otro). */
export const SHRINKAGE_REASONS = ['Vencido', 'Roto o dañado', 'Robo o faltante', 'Consumo interno'] as const;

/** Tipos que exigen un motivo escrito (quedan en la auditoría). */
export const REASON_REQUIRED: readonly MovementType[] = ['adjustment', 'shrinkage'];

/** Tipos que aceptan cantidad 0: contar 0 o cargar stock inicial 0 empieza el control de stock. */
export const ZERO_QTY_TYPES: readonly MovementType[] = ['initial', 'count'];

/** ¿La cantidad (con signo) es válida para el tipo? Cero solo en stock inicial y toma de inventario. */
export function isValidMovementQty(type: MovementType, qtyMilli: number): boolean {
  if (!Number.isSafeInteger(qtyMilli)) return false;
  if (qtyMilli === 0) return ZERO_QTY_TYPES.includes(type);
  const { sign } = MOVEMENT_TYPES[type];
  return sign === 'any' || (sign === 'in' ? qtyMilli > 0 : qtyMilli < 0);
}

/** Cantidad con el signo que corresponde: compras suman, mermas restan, etc. */
export function signedQty(type: MovementType, absMilli: number, direction: 'in' | 'out' = 'in'): number {
  const { sign } = MOVEMENT_TYPES[type];
  const abs = Math.abs(absMilli);
  if (sign === 'in') return abs;
  if (sign === 'out') return -abs;
  return direction === 'in' ? abs : -abs;
}

/** Movimiento de una toma de inventario: lo que hay que sumar/restar para llegar a lo contado. */
export function countDelta(systemMilli: number, countedMilli: number): number {
  return countedMilli - systemMilli;
}

export type StockStatus = 'negative' | 'out' | 'low' | 'ok' | 'untracked';

/**
 * Estado del stock de una variante. "untracked": nunca tuvo movimientos (el negocio
 * no lleva stock de ese producto), así no se llena la pantalla de alertas falsas.
 */
export function stockStatus(qtyMilli: number, minStockMilli: number | null, hasMovements: boolean): StockStatus {
  if (!hasMovements) return 'untracked';
  if (qtyMilli < 0) return 'negative';
  if (qtyMilli === 0) return 'out';
  if (minStockMilli !== null && qtyMilli <= minStockMilli) return 'low';
  return 'ok';
}
